<script setup lang="ts">

import GenericIconWithColoredNumber from '@/components/Elements/Images/Icons/GenericIconWithColoredNumber.vue'
import { useShipDesignCreationStore } from '@/stores/shipDesignCreationStore.ts'
import { computed } from 'vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useAssetStore } from '@/stores/assetStore.ts'

/**
 * Stores / models
 */
const shipDesignCreationStore = useShipDesignCreationStore()
const gameStore = useGameStore()
const assetStore = useAssetStore()

/**
 * Function
 */
const save = () => {
  gameStore.modalType = 'createShipDesignSave'
}

const importDesign = () => {
  // Create a hidden file input
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.ferion'

  input.onchange = (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]

    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string
        const importData = JSON.parse(content)

        // Validate the data structure
        if (!importData.componentCodes || !Array.isArray(importData.componentCodes)) {
          gameStore.setTinyErrorToastMessage('Invalid file format: missing componentCodes array')
          return
        }

        // Verify player has access to all components
        const unavailableComponents: string[] = []
        const availableComponents = []

        for (const componentCode of importData.componentCodes) {
          const component = assetStore.findShipComponentByCode(componentCode)

          if (!component) {
            unavailableComponents.push(componentCode)
            continue
          }

          // Check if player has access to this component
          const hasAccess = Array.from(gameStore.playerShipComponents.values()).includes(componentCode)

          if (!hasAccess) {
            unavailableComponents.push(componentCode)
          } else {
            availableComponents.push(component)
          }
        }

        // If any components are unavailable, show error
        if (unavailableComponents.length > 0) {
          gameStore.setTinyErrorToastMessage(`Missing ${unavailableComponents.length} component(s). You don't have access to all required components.`)
          return
        }

        // All components are available - clear current design and import
        shipDesignCreationStore.resetDesign()

        // Set the imported design name
        const designName = importData.name || ''
        shipDesignCreationStore.designName = designName

        // Add all components
        for (const component of availableComponents) {
          shipDesignCreationStore.addComponent(component, false)
        }

        // Recalculate the design stats
        shipDesignCreationStore.recalculateDesign()

        // Success message
        gameStore.setTinySuccessToastMessage(`Import successful! ${availableComponents.length} components loaded${designName ? ` from "${designName}"` : ''}`)

      } catch (error) {
        gameStore.setTinyErrorToastMessage('Failed to import design: Invalid file format')
      }
    }

    reader.readAsText(file)
  }

  input.click()
}

/**
 * Computed
 */
//Calculate the percentage of the ship size in use (make reactive)
const shipSizePercentage = computed(() => {
  if(shipDesignCreationStore.shipFrame === 0) return 0
  const percentage = (shipDesignCreationStore.shipMass / shipDesignCreationStore.shipFrame) * 100
  if(percentage > 100) return 100
  return percentage
})

const overloaded = computed(() => {
  return shipDesignCreationStore.shipFrame < shipDesignCreationStore.shipMass
})

</script>

<template>
  <div class="">
    <div class="flex pb-1">
      <div class="flex-shrink-0 rounded bg-slate-800 p-2 mr-1 flex items-center justify-center">
        <ActionButton @click="importDesign" :for-icon="true">
          <GenericIcon type="import" tooltip="Import design"/>
        </ActionButton>
      </div>
      <div class="p-2 rounded bg-slate-800 flex justify-center flex-grow">
        <div class="flex-shrink-0 px-2 self-center">
          <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipMass" type="mass" :shortFormat="false"/>
        </div>
        <div class="flex-grow px-1 self-center">
          <div class="w-full rounded h-5 bg-gray-700">
            <div :class="{
              'bg-blue-600': shipDesignCreationStore.shipFrame > shipDesignCreationStore.shipMass,
              'bg-red-600': shipDesignCreationStore.shipFrame < shipDesignCreationStore.shipMass,
              'bg-green-600': shipDesignCreationStore.shipFrame === shipDesignCreationStore.shipMass,
              'h-5 rounded' : true
            }" :style="'width: '+ shipSizePercentage +'%'"></div>
          </div>
        </div>
        <div class="flex-shrink-0 px-2 py-1 self-center">
          <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipFrame" type="frame" :shortFormat="false"/>
        </div>
      </div>
      <div class="flex-shrink-0 rounded bg-slate-800 p-2 ml-1" v-if="shipDesignCreationStore.hasEngine && !overloaded">
        <ActionButton @click="save">Save</ActionButton>
      </div>
    </div>
    <div class="grid grid-cols-5 gap-1">
      <div class="p-2 rounded bg-slate-800 flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipAttackShips" type="attack_against_ships"/>
      </div>
      <div class="p-2 rounded bg-slate-800 flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipAttackPlanets" type="attack_against_planets"/>
      </div>
      <div class="p-2 rounded bg-slate-800 flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipShields" type="shields"/>
      </div>
      <div class="p-2 rounded bg-slate-800 flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipHitPoints" type="hit_points"/>
      </div>
      <div class="p-2 rounded bg-slate-800 flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipMaxSpeed" type="max_speed"/>
      </div>
      <div class="p-2 rounded bg-slate-800 flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipScanningRange" type="scanning_range"/>
      </div>
      <div class="p-2 rounded bg-slate-800 flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipRepair" type="repair"/>
      </div>
      <div class="p-2 rounded bg-slate-800 flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipProductionCost" type="production"/>
      </div>
      <div class="p-2 rounded bg-slate-800 flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.maintenanceCost" type="credit"/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>